import { Form, TextArea, useForm } from '@/components/form';
import { createModal } from '@/hooks/system/create-modal';
import { api } from '@/services/api/services-api';
import { sentryCaptureException } from '@/utils/sentry';
import { useCopilotTool } from '@customerio/hydra-copilot/react';
import { Button, Modal, ModalBody, ModalFooter, useSnackbars } from '@customerio/pluma-components/react';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import { useAuth } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';

export const useFeedbackModal = createModal('feedback', {
  type: 'ephemeral',
});

export function ShowFeedbackTool() {
  const auth = useAuth();
  const feedbackModal = useFeedbackModal();
  const snackbars = useSnackbars();
  const analytics = useAnalytics();

  const form = useForm({
    defaultValues: {
      feedback: '',
    },
    validators: {
      onChange: z.object({
        feedback: z.string().min(1, 'Feedback is required'),
      }),
    },
    async onSubmit(values) {
      try {
        await api.submitFeedback({
          params: { accountId: auth.account.id },
          body: {
            replyTo: auth.user.email,
            accountId: auth.account.id,
            userFirstName: auth.user.first_name,
            userLastName: auth.user.last_name,
            browser: navigator.userAgent,
            url: window.location.href,
            windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
            criticism: 'Feedback from AI Assistant',
            praise: values.value.feedback,
          },
        });

        analytics.track('aiAssistant', 'feedbackFormSubmitted', {
          Feedback: values.value.feedback,
        });

        handleClose();
      } catch (error) {
        sentryCaptureException(error);
        snackbars.create({
          message: 'Failed to submit feedback. Please try again.',
        });
      }
    },
  });

  useCopilotTool({
    type: 'client',
    name: 'show_feedback_form',
    description: `
      Shows a feedback form for the user to provide feedback or to request a feature. If at any point the user asks for feedback or requests a feature, you should show the feedback form.
      
      <sample-chat>
      User: I want to give feedback on this experience
      Model: show_feedback_form({ feedback: "INSERT A SUMMARY OF THE CHAT HERE", })
      </sample-chat>
        
      If the user asks for you to do something that you are 100% sure you cannot do, you should also show the feedback form.

      <sample-chat>
      User: I want to create a campaign.
      Model: I am unable to create a new campaign for you, but I can help you with other tasks. Do you want to request that feature?
      User: Yes
      Model: show_feedback_form({ feedback: "I want to create a new campaign but the Assistant is not helping me." })
      </sample-chat>
    `,
    requestSchema: zodToJsonSchema(
      z.object({
        feedback: z
          .string()
          .describe(
            'The summary of the feedback provided by the user, written in first person (e.g., "I need help with..." or "I would like..."). You should do your best to figure out what the user wants to say.',
          ),
      }),
    ),
    handler({ args }) {
      form.setFieldValue('feedback', args.feedback);
      feedbackModal.show();
      return true;
    },
  });

  const handleClose = () => {
    feedbackModal.close();
    form.reset();
  };

  return (
    <Modal isOpen={feedbackModal.isOpen} onClose={handleClose} title="Send us your feedback" size="md">
      <Form api={form}>
        <ModalBody>
          <TextArea
            api={form}
            name="feedback"
            label="What would you like to tell us?"
            placeholder="What can we improve?"
            minRows={4}
            maxRows={8}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Submit Feedback
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
